import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DacGeneralParamsRepository } from '@/store/dac-general-params-state/dac-general-params-state.repository';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacGeneralParamsService } from '../dac-general-params.service';
import { ParamGeneralFieldsService } from '../param-general-fields.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { BlockVariant, DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getCreateDacGeneralParamsFormObject } from './utils/new-dac-general-params.form-object';
import { paramGeneralFieldStoreFactory } from '@/store/dac-general-params-state/param-general-field-state/param-general-field.store';
import { ParamGeneralDacBatchDto, ParametrageGeneralDacDto } from '@/types';
import { dacGeneralParamsSchema } from '@/validations/dac-general-params.validation';

@Component({
  selector: 'sm-new-dac-general-params',
  imports: [PageHeaderComponent, FormBuilderComponent, BlockBuilderComponent],
  templateUrl: './new-dac-general-params.component.html',
})
export class NewDacGeneralParamsComponent implements OnInit, OnDestroy {
  dacGeneralParamsRepository = inject(DacGeneralParamsRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  dacGeneralParamsService = inject(DacGeneralParamsService);
  paramGeneralFieldsService = inject(ParamGeneralFieldsService);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);

  breadcrumb = [
    { label: 'Paramètrage', routerLink: ['/settings'] },
    { label: 'dacGeneralParams.title', routerLink: ['/settings', 'general-parameters'] },
    { label: 'dacGeneralParams.pages.new' },
  ];

  title = 'dacGeneralParams.pages.new';

  loading$ = new BehaviorSubject<boolean>(false);
  dynaDocsModelOptions: SelectOption[] = [];

  formObject: DynamicForm = getFormBuilderObjectFactory();

  controlBlocks: DynamicBlocks = {
    '1': [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'common.add',
          onClick: () => this.save(),
          loading: this.loading$,
        },
      },
    ],
    '2': [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'common.cancel',
          onClick: () => this.router.navigate(['/settings', 'general-parameters']),
          severity: 'secondary',
        },
      },
    ],
  };

  constructor() {
    this.multiFormAccordionService.formFactory = (id: string) => paramGeneralFieldStoreFactory(id);
    this.multiFormAccordionService.init();
  }

  ngOnInit() {
    this.paramGeneralFieldsService.initialize();

    forkJoin({
      dynaDocsModels: this.dacGeneralParamsService.getDynaDocsModels(),
    }).subscribe({
      next: ({ dynaDocsModels }) => {
        this.dynaDocsModelOptions = dynaDocsModels.map(dm => ({ name: dm.description, code: dm.dynaDocsModelId }));

        this.formObject = getCreateDacGeneralParamsFormObject({
          store: this.dacGeneralParamsRepository,
          dynaDocsModelOptions: this.dynaDocsModelOptions,
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors du chargement des options',
        });
      },
    });
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.dacGeneralParamsRepository.reset();
  }

  save() {
    const dto = this.dacGeneralParamsRepository.get<ParametrageGeneralDacDto>('createDto');
    const result = dacGeneralParamsSchema.safeParse(dto);
    const fieldsValid = this.paramGeneralFieldsService.validateAllFieldDtos();

    if (result.success && fieldsValid) {
      const fieldDtos = this.paramGeneralFieldsService.getAllFieldDtos();
      const dtos: ParamGeneralDacBatchDto[] = fieldDtos.map(field => ({
        dynaDocsModels: dto.dynaDocsModelIds?.map(id => ({ dynaDocsModelId: id, paramGeneralStatus: 'ACTIF' })),
        ...field,
      }));
      this.loading$.next(true);
      this.dacGeneralParamsService.addBatch(dtos).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.dacGeneralParamsRepository.reset();
          this.router.navigate(['/settings', 'general-parameters']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('dacGeneralParams.add.success'),
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error?.message || this.translate.instant('dacGeneralParams.add.error'),
          });
          this.loading$.next(false);
        },
      });
    } else if (result.error) {
      this.dacGeneralParamsRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }
}
